import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Plus, Trash2, User, Mail, Shield } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const SubAdminManager = () => {
  const [subAdmins, setSubAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [newSubAdmin, setNewSubAdmin] = useState({
    name: '',
    email: '',
    password: ''
  });
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');

  const fetchSubAdmins = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/admin/subadmins`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubAdmins(response.data.subadmins || []);
    } catch (err) {
      console.error('Error fetching subadmins:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSubAdmins();
  }, [fetchSubAdmins]);

  const handleCreate = async () => {
    if (!newSubAdmin.name || !newSubAdmin.email || !newSubAdmin.password) {
      setError('Toate câmpurile sunt obligatorii');
      return;
    }

    if (newSubAdmin.password.length < 6) {
      setError('Parola trebuie să aibă minim 6 caractere');
      return;
    }

    setCreating(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/api/admin/subadmins`,
        newSubAdmin,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setIsDialogOpen(false);
      setNewSubAdmin({ name: '', email: '', password: '' });
      fetchSubAdmins();
    } catch (err) {
      setError(err.response?.data?.detail || 'Eroare la crearea sub-administratorului');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Sigur doriți să ștergeți acest sub-administrator?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/admin/subadmins/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchSubAdmins();
    } catch (err) {
      console.error('Error deleting subadmin:', err);
      alert('Eroare la ștergerea sub-administratorului');
    }
  };

  if (loading) {
    return <div className="text-center py-4">Se încarcă...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-sm text-gray-600">
          {subAdmins.length} sub-administrator{subAdmins.length !== 1 ? 'i' : ''} înregistrați
        </p>
        <Button onClick={() => setIsDialogOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Adaugă Sub-Administrator
        </Button>
      </div>

      {subAdmins.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          Nu există sub-administratori. Adăugați primul sub-administrator pentru a delega gestionarea băncilor.
        </div>
      ) : (
        <div className="grid gap-4">
          {subAdmins.map((subAdmin) => (
            <Card key={subAdmin.id} className="border">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                      <Shield className="h-5 w-5 text-purple-600" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{subAdmin.name}</span>
                        <Badge variant="secondary" className="text-xs">Sub-Admin</Badge>
                      </div>
                      <div className="flex items-center gap-4 text-sm text-gray-500 mt-1">
                        <span className="flex items-center gap-1">
                          <Mail className="h-3 w-3" />
                          {subAdmin.email}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-400">
                      Creat: {new Date(subAdmin.createdAt).toLocaleDateString('ro-RO')}
                    </span>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      onClick={() => handleDelete(subAdmin.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Dialog pentru adăugare sub-administrator */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Adaugă Sub-Administrator</DialogTitle>
            <DialogDescription>
              Sub-administratorul va putea gestiona băncile și produsele, dar nu va avea acces la gestionarea intermediarilor.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded text-red-600 text-sm">
                {error}
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="name">Nume complet *</Label>
              <Input
                id="name"
                value={newSubAdmin.name}
                onChange={(e) => setNewSubAdmin({ ...newSubAdmin, name: e.target.value })}
                placeholder="ex: Ion Popescu"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                value={newSubAdmin.email}
                onChange={(e) => setNewSubAdmin({ ...newSubAdmin, email: e.target.value })}
                placeholder="ex: subadmin@brix.ro"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Parolă *</Label>
              <Input
                id="password"
                type="password"
                value={newSubAdmin.password}
                onChange={(e) => setNewSubAdmin({ ...newSubAdmin, password: e.target.value })}
                placeholder="Minim 6 caractere"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
              Anulează
            </Button>
            <Button onClick={handleCreate} disabled={creating}>
              {creating ? 'Se creează...' : 'Creează Sub-Administrator'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SubAdminManager;
